import Backbone from "backbone";
import _ from "underscore";
import DriveView from "../../../../DriveView";
import DutyStatusLog  from "../../../../model/DutyStatusLog";
import { isNoVehicle, getCurrentVehicle, setCurrentVehicle } from "../../../../utils/device";
import idSerializer from "geotab-uuid";
import { getServerDate } from "../../../../utils/storage/date";
import rison from "rison";
import { dispatcher, DispatcherEvents } from "../../../../utils/dispatcher";
import { createElement, empty } from "../../../../utils/vanilla";
import pageHTML from "./logout.tmpl";
import { getLastPosition } from "../../../../utils/location";
import { handleApi } from "../../../../utils/error";
import { Dialog } from "../../../framework/dialog";
import { PageLevel } from "../../../../store/mobile/types";
import { CancelablePromise as Promise } from "../../../../utils/cancelablePromise";
import { getCustomExemptHOSName } from "../../../../utils/hosHelpers";
import SelectList, { ISelectListOptions } from "../../../framework/selectList";
import { BaseCache, makeDeviceCache } from "../../../../utils/storage/cache";
import { Device } from "../../../../model/Device";
import * as logger from "../../../../utils/logger";
import { FeatureFlagName } from "../../../../utils/featureFlagName";
import type { THosOrigin } from "hos-tools";
import { rateAppService } from "../../../../services/rateAppService";

const LOGOUT_BUTTON = "hos-logout-ok";
const CLOCK_OUT_BUTTON = "hos-logout-clock-out";

interface ILogoutModel {
    currentStatus?: string | null;
    nextStatus?: string | null;
}

class LogoutModel extends Backbone.Model {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(options: ILogoutModel) {
        super(options);
    }

    default(): ILogoutModel {
        return {
            currentStatus: null,
            nextStatus: null
        };
    }
}

    export default class HosLogoutView extends DriveView<LogoutModel> {

        protected strings = [
            "You are currently in the '{0}' status.",
            "Saving in progress",
            "Set new status",
            "Update your status before logging out:",
            "Confirm or update your status before continuing:"
        ];
        private readonly template = _.template(pageHTML);
        private readonly currentVehicle: IIdEntity | null;
        private readonly deviceCache: BaseCache<IDevice | Device>;
        private backToDashboardDialog: Dialog;

        constructor(options) {
            super(options, {
                title: "Update Status",
                activeSection: "hos",
                pageLevel: PageLevel.RETURN_DASHBOARD
            });
            let logs = this.app.dutyStatusLogService.getHOSLogs().filter(hosLog => hosLog.isStandardStatusDriverLog());
            let latestLog = this.app.dutyStatusLogService.getLatestLog(logs);
            let latestStatus = latestLog ? latestLog.status : null;

            this.model = new LogoutModel({
                currentStatus: latestStatus,
                nextStatus: "OFF"
            });

            // if they can't use hos or they can't attempt hos or they can attempt hos but have a vehicle
            if (!(this.app.securityService.can("useHos") || (this.app.securityService.can("attemptHos") && isNoVehicle()))) {
                logger.event(logger.Event.DEBUG_LOGINVIEW_NAVIGATE, "HosLogoutView: User can't use HOS or can but has no vehicle. Redirect out.");
                this.app.router.navigate(`login,${ rison.encode({ loggingOut: true }) }`, true);
                return;
            }

            this.listenTo(this.model, "change", () => this.render());

            this.currentVehicle = getCurrentVehicle();
            this.deviceCache = makeDeviceCache(this.app.store);
        }

        /* eslint-disable @typescript-eslint/unbound-method */
        events() {
            return {
                [`click #${ LOGOUT_BUTTON }`]: this.logout,
                [`click #${ CLOCK_OUT_BUTTON }`]: this.backToDashboard
            } as Backbone.EventsHash;
        }
        /* eslint-enable @typescript-eslint/unbound-method */

        render() {
            let switchStatusHtml = createElement("div", undefined, "activeDriverSelection");
            let statusArray = [["OFF", "Off Duty"], ["ON", "On Duty"]];

            let radioListItems: ISelectListOptions[] = statusArray.map(s => {
                return {
                    id: s[0],
                    name: s[1],
                    isSelected: s[0] === this.model.get("nextStatus"),
                    template: `<span class="hosStatusLabel ${ s[0] }" id="${ s[0] }-description">${ s[0] }</span>`
                };
            });

            let selectList = new SelectList({
                el: switchStatusHtml,
                selectCallback: item => {
                    this.model.set("nextStatus", item?.id);
                    this.render();
                },
                title: "Update Status"
            });
            selectList.render(radioListItems);

            let currentStatus = this.model.get("currentStatus");
            if (currentStatus === "PC_Exempted") {
                currentStatus = getCustomExemptHOSName(this.app.store.getState().systemSettings);
            }

            void this.display(this.template({
                statusMessage: [
                    this.getString(this.strings[0]).replace("{0}", currentStatus),
                    currentStatus === "OFF" ? this.getString(this.strings[4]) : this.getString(this.strings[3])
                ],
                displayClockInClockOut: this.app.securityService.can("viewDriverClockInOut"),
                model: this.model.toJSON()
            })).then(() => {
                let chooseStatusElement = document.getElementById("hos-logout-choose-status");
                if (chooseStatusElement) {
                    empty(chooseStatusElement);
                    chooseStatusElement.appendChild(switchStatusHtml);
                }
            });

            return this;
        }

        getLocation(): Promise<IAddressLookupData | undefined> {
            // Wrapped to allow unit test to work
            return getLastPosition(this.app.store)
                .then(position => Promise.resolve({ location: { x: position.coords.longitude, y: position.coords.latitude }}))
                .catch(e => {
                    e && handleApi(e);
                    return Promise.resolve(undefined);
                });
        }

        finish(isLogout: boolean, assignedToVehicle: boolean) {
            let toggleButton = (btn: HTMLInputElement, disabledText: string) => {
                let previousText: string | null;
                if (btn) {
                    previousText = btn.textContent;
                }
                return () => {
                    if (!btn) {
                        return;
                    }

                    if (btn.disabled) {
                        btn.disabled = false;
                        btn.textContent = previousText;
                    } else {
                        btn.disabled = true;
                        btn.textContent = disabledText;
                    }
                };
            };

            let savingText = this.getString("Saving in progress");
            let logoutToggle = toggleButton(document.getElementById(LOGOUT_BUTTON) as HTMLInputElement, savingText);
            let backToDashboardToggle = toggleButton(document.getElementById(CLOCK_OUT_BUTTON) as HTMLInputElement, savingText);

            logoutToggle();
            backToDashboardToggle();

            // Capture user reference once at the start, before any async operations
            const currentUser = this.app.userService.getCurrentUser();

            return Promise.resolve()
                .then(() => {
                    // Skip if we're keeping vehicle, or logging out. This code is performed on ui/login as well,
                    // so don't double unassign vehicle.
                    if (assignedToVehicle || isLogout) {
                        return Promise.resolve();
                    }

                    // Check if there are other active drivers BEFORE creating DriverChange
                    // If there are, skip the entire vehicle unassign flow to preserve their vehicle state
                    const isKillswitchEnabled = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.DISABLE_CODRIVER_LOGOUT_VEHICLE_STATE_FIX);
                    const hasOtherActiveDrivers = this.app.userService.getActiveUserNames().length > 1;

                    if (!isKillswitchEnabled && hasOtherActiveDrivers) {
                        // Preserve vehicle state for other active drivers - skip DriverChange creation
                        return Promise.resolve();
                    }

                    let oldVehicle = getCurrentVehicle();
                    let newVehicle = { id: "NoDeviceId" };

                    return this.app.vehicleService.setDriverVehicle(null, "UnknownDriverId", true)
                        .then(() => {
                            this.deviceCache.add([newVehicle]);
                            setCurrentVehicle(newVehicle);
                            return this.app.manager.onVehicleSelected(this.app.context, newVehicle, oldVehicle, true);
                        });
                })
                .then(() => {
                    const createHosLog = currentUser && this.model.get("currentStatus") !== this.model.get("nextStatus") || false;

                    if (!createHosLog || !currentUser) {
                        return Promise.resolve();
                    }

                    let deviceId = this.currentVehicle && this.currentVehicle.id ? this.currentVehicle.id : "NoDeviceId";
                    let currentStatus = this.model.get("currentStatus");
                    let nextStatus = this.model.get("nextStatus");
                    let logDateTime = getServerDate();

                    // If logging out from PC/YM, create SDC + status logs (DSWIE only if FF enabled and ongoing driving)
                    if (deviceId !== "NoDeviceId" && (currentStatus === "PC" || currentStatus === "YM")) {
                        let latestLog = this.app.dutyStatusLogService.getLatestLog(this.app.dutyStatusLogService.getHOSLogs());
                        let exemptionEndingLogs = this.app.dutyStatusLogService.getLogsToEndDrivingExemption(currentUser as IUser, latestLog, logDateTime, "Manual", nextStatus, true);
                        return Promise.all(exemptionEndingLogs.map(log => this.app.sync.dutyStatusLogs.addItem(log, true)));
                    }

                    let newLog = new DutyStatusLog({
                        id: idSerializer.get(),
                        dateTime: logDateTime.toISOString(),
                        device: { id: deviceId },
                        driver: { id: currentUser.id },
                        coDrivers: this.app.userService.getCoDrivers(currentUser.name),
                        status: nextStatus,
                        origin: "Manual" as THosOrigin,
                        verifyDateTime: logDateTime.toISOString()
                    });

                    if (deviceId !== "NoDeviceId") {
                        return this.app.sync.dutyStatusLogs.addItem(newLog, true);
                    }

                    return this.getLocation()
                        .then(location => {
                            if (location) {
                                newLog.location = location;
                            }
                            return this.app.sync.dutyStatusLogs.addItem(newLog, true);
                        });
                })
                .then(() => {
                    if (!isLogout) {
                        return this.app.sync.synchronize();
                    }
                    return Promise.resolve();
                })
                .then(() => {
                    if (this.backToDashboardDialog) {
                        this.backToDashboardDialog.waiting.stop();
                    }

                    // Prompt for app review on logout (if user is eligible)
                    if (isLogout) {
                        rateAppService.checkAndPromptReview(currentUser, this.app.api, this.app.userService);
                    }

                    dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep, isLogout);
                })
                .catch(e => {
                    // Possibility of this if they choose ON for some reason while logging out
                    logoutToggle();
                    backToDashboardToggle();

                    if (this.backToDashboardDialog) {
                        this.backToDashboardDialog.waiting.stop();
                    }

                    e && handleApi(e);
                });
        }

        logout() {
            return this.finish(true, false);
        }

        backToDashboard() {
            let user = this.app.userService.getCurrentUser();
            let driver = this.app.userService.getDriversSeatUser();

            // If a codriver or no vehicle chosen
            if ((user && driver && user.id !== driver.id) || (!this.currentVehicle || this.currentVehicle.id === "NoDeviceId")) {
                return this.finish(false, true);
            }

            this.backToDashboardDialog = new Dialog(
                "driverchange-dialog",
                "Do you want to remain assigned to your vehicle?",
                this.translator,
                "Vehicle assignment",
                [
                    {
                        callback: () => {
                            return this.finish(false, false);
                        },
                        text: "No",
                        className: "button_secondary",
                        spinner: true,
                        id: "driverchange-dialog-unassign"
                    },
                    {
                        callback: () => {
                            return this.finish(false, true);
                        },
                        text: "Yes",
                        spinner: true,
                        id: "driverchange-dialog-remain"
                    }
                ],
                {
                    isNightMode: this.app.store.getState().data.nightMode
                }
            );
            return this.backToDashboardDialog.open();
        }
    }
